import { Trans, useTranslation } from 'react-i18next';

import { Tag } from '~/components/Tag';
import { cn } from '~/lib/cn';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

import type { TimeRange } from './TimeRangeLabel';
import { getBenchmarkComparisonPercentage } from './utils';

interface TrackPerformanceTooltipProps {
  metricValue: number;
  metricType?: string;
  metricTimeRange?: TimeRange;
  defaultBenchmark: number;
  adjustedBenchmark?: number;
  adjustedBy?: string;
  adjustmentReason?: string;
}

export function TrackPerformanceTooltip({
  metricValue,
  metricType = 'streams',
  metricTimeRange = 'RTD',
  defaultBenchmark,
  adjustedBenchmark,
  adjustedBy,
  adjustmentReason,
}: TrackPerformanceTooltipProps) {
  const { t } = useTranslation('roster');

  const [isSetBenchmarksEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgTrackSetBenchmarks,
  ]);

  function compareBenchmark(
    benchmark: number,
    benchmarkType: 'Default' | 'Adjusted',
  ) {
    const percentAgainstBenchmark = getBenchmarkComparisonPercentage(
      metricValue,
      benchmark,
    );
    const negativeAdjPercent =
      percentAgainstBenchmark < 0 ? Math.abs(percentAgainstBenchmark) : 0;
    const positiveAdjPercent =
      percentAgainstBenchmark > 0 ? percentAgainstBenchmark : 0;
    const isNegative = percentAgainstBenchmark < 0;
    const displayPercent = isNegative ? negativeAdjPercent : positiveAdjPercent;
    const isZero = displayPercent === 0;

    let textColorClassName;
    let benchmarkTextKey;
    const benchmarkTypeTextColor =
      benchmarkType === 'Default'
        ? 'text-tertiary-default'
        : 'text-primary-default';

    if (isZero) {
      textColorClassName = 'text-subtle';
      benchmarkTextKey = isSetBenchmarksEnabled
        ? `benchmarks.percentAgainst${benchmarkType}Benchmark`
        : 'benchmarks.percentAgainstBenchmarkValue';
    } else if (isNegative) {
      textColorClassName = 'text-error-default';
      benchmarkTextKey = isSetBenchmarksEnabled
        ? `benchmarks.percentBelow${benchmarkType}Benchmark`
        : 'benchmarks.percentBelowBenchmarkValue';
    } else {
      textColorClassName = 'text-success-default';
      benchmarkTextKey = isSetBenchmarksEnabled
        ? `benchmarks.percentAbove${benchmarkType}Benchmark`
        : 'benchmarks.percentAboveBenchmarkValue';
    }

    return (
      <div className="text-caption-md">
        <Trans
          t={t}
          i18nKey={benchmarkTextKey}
          components={{
            percent: (
              <span className={cn('font-semibold', textColorClassName)} />
            ),
            benchmark: (
              <span
                className={cn(
                  'font-semibold',
                  isSetBenchmarksEnabled && benchmarkTypeTextColor,
                )}
              />
            ),
          }}
          values={{
            percent: displayPercent.toLocaleString(),
            benchmark: benchmark.toLocaleString(),
          }}
        />
      </div>
    );
  }

  let benchmarkContent: React.ReactNode = null;

  if (metricTimeRange === 'RTD') {
    const hasAdjustment =
      adjustedBenchmark !== undefined &&
      adjustedBy !== undefined &&
      adjustmentReason !== undefined;

    if (hasAdjustment) {
      benchmarkContent = (
        <>
          {compareBenchmark(adjustedBenchmark, 'Adjusted')}
          {compareBenchmark(defaultBenchmark, 'Default')}
          <div className="h-[0.75em]" />
          <Tag
            variant="static"
            disabled
            className="py-xs text-caption-md font-semibold outline-none"
          >
            {t('benchmarks.adjustedByName', { name: adjustedBy })}
          </Tag>
          <div className="text-caption-md italic">{`"${t(adjustmentReason)}"`}</div>
        </>
      );
    } else {
      benchmarkContent = compareBenchmark(defaultBenchmark, 'Default');
    }
  }

  return (
    <div className="flex flex-col items-start gap-sm rounded-sm">
      <div className="flex items-end gap-xs text-nowrap">
        <span>{t(`trackList.header.${metricType}`)}</span>
        <span className="text-caption-md font-bold">{metricTimeRange}</span>
      </div>
      <div className="text-body-md font-semibold">
        {metricValue.toLocaleString()}
      </div>
      {benchmarkContent}
    </div>
  );
}

TrackPerformanceTooltip.displayName = 'TrackPerformanceTooltip';
